import { useInfiniteQuery, useMutation, useQuery } from 'react-query';
import { ListManufacturersQuery, Manufacturer } from './Manufacturers.types';
import {
  detailManufacturers,
  listManufacturers,
  statusManufacturers,
} from './Manufacturers.service';
import { DEFAULT_ORDER, DEFAULT_ROWS } from '..';
import notificationError from '@/utils/functions/notificationError';
import { queryClient } from '@/Root';
import { useQueryChanel } from '@/contexts/QueryChanelProvider';

const QUERY_MANUFACTURE_LIST_KEY = 'manufacturers/list';
const QUERY_MANUFACTURE_DETAIL_KEY = 'manufacturers/detail';

export function useManufacturers(
  params?: ListManufacturersQuery,
  enabled = true,
) {
  const cache = queryClient.getQueryData([
    QUERY_MANUFACTURE_LIST_KEY,
    { ...params },
  ]);

  return useInfiniteQuery(
    [QUERY_MANUFACTURE_LIST_KEY, { ...params }],
    ({ pageParam }) => {
      const req: ListManufacturersQuery = {
        nextToken: pageParam,
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listManufacturers(req);
    },
    {
      enabled: enabled && !cache,
      getNextPageParam: (lastPage) => lastPage.nextToken,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useManufacturersMutation() {
  return useMutation(
    (params?: ListManufacturersQuery) => {
      const req: ListManufacturersQuery = {
        order: params?.order || DEFAULT_ORDER,
        rows: params?.rows || DEFAULT_ROWS,
        ...params,
      };

      return listManufacturers(req);
    },
    {
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useManufacturerStatus(active: boolean) {
  const queryChanel = useQueryChanel();

  return useMutation(
    (manufacturerId: number) => statusManufacturers(manufacturerId, active),
    {
      onSuccess() {
        queryChanel({ resetQueries: [[QUERY_MANUFACTURE_LIST_KEY]] });
      },
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useManufacturerDetail(id?: number) {
  return useQuery(
    [QUERY_MANUFACTURE_DETAIL_KEY, id],
    () => (id ? detailManufacturers(id) : undefined),
    {
      enabled: !!id,
      onError(error) {
        notificationError(error);
      },
    },
  );
}

export function useManufacturerDetailMutation() {
  return useMutation(
    async (id: number) => {
      const cache = queryClient.getQueryData<Manufacturer>([
        QUERY_MANUFACTURE_DETAIL_KEY,
        id,
      ]);

      if (!!cache) return cache;

      return detailManufacturers(id);
    },
    {
      onError(error) {
        notificationError(error);
      },
    },
  );
}
